import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import ProtectedLayout from 'src/routes/ProtectedLayout';
import PermissionGuard from 'src/routes/PermissionGuard';

const Loader = (Component: React.ComponentType<any>) => (props: any) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

const Overview = Loader(lazy(() => import('src/content/overview')));
const Dashboard = Loader(lazy(() => import('src/content/applications/Dashboard')));
const Status404 = Loader(lazy(() => import('src/content/pages/Status/Status404')));
const Status500 = Loader(lazy(() => import('src/content/pages/Status/Status500')));
const StatusComingSoon = Loader(lazy(() => import('src/content/pages/Status/ComingSoon')));
const StatusMaintenance = Loader(lazy(() => import('src/content/pages/Status/Maintenance')));
const LoginPage = Loader(lazy(() => import('src/content/pages/Login')));
const SeleccionarSucursalPage = Loader(lazy(() => import('src/content/pages/SeleccionarSucursal')));

const EmpresasPage = Loader(lazy(() => import('src/content/applications/Empresas')));
const MonedasPage = Loader(lazy(() => import('src/content/applications/Monedas')));
const SucursalesPage = Loader(lazy(() => import('src/content/applications/Sucursales')));
const UsuariosPage = Loader(lazy(() => import('src/content/applications/UsuariosRest')));
const RolesPage = Loader(lazy(() => import('src/content/applications/Roles')));
const PermisosPage = Loader(lazy(() => import('src/content/applications/Permisos')));
const AreasPage = Loader(lazy(() => import('src/content/applications/Areas')));
const CategoriasPage = Loader(lazy(() => import('src/content/applications/Categorias')));
const ProductosPage = Loader(lazy(() => import('src/content/applications/Productos')));
const MesasPage = Loader(lazy(() => import('src/content/applications/Mesas')));
const ZonasPage = Loader(lazy(() => import('src/content/applications/Zonas')));
const MetodosPagoPage = Loader(lazy(() => import('src/content/applications/MetodosPago')));
const MeseroPedidosPage = Loader(lazy(() => import('src/content/applications/MeseroPedidos'))); 
const VentasDiariasPage = Loader(lazy(() => import('src/content/applications/Reportes/VentasDiarias')));
const PedidosAnuladosPage = Loader(lazy(() => import('src/content/applications/Reportes/PedidosAnulados')));
const HistorialPedidosPage = Loader(lazy(() => import('src/content/applications/Reportes/HistorialPedidos')));
const POSPage = Loader(lazy(() => import('src/content/applications/POS')));
const ComandasPage = Loader(lazy(() => import('src/content/applications/Comandas')));
const ComandasPorPedidoPage = Loader(lazy(() => import('src/content/applications/Comandas/ComandasPorPedido')));
const PedidosListTVPage = Loader(lazy(() => import('src/content/applications/Pedidos/PedidosListTV')));
const ProduccionTVPage = Loader(lazy(() => import('src/content/applications/Pedidos/ProduccionTV')));
const TokensTvPage = Loader(lazy(() => import('src/content/applications/Administracion/TokensTv')));

const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'pedidos-listos-tv', element: <PedidosListTVPage /> },
      { path: 'produccion-tv', element: <ProduccionTVPage /> },
      {
        element: (
          <ProtectedLayout>
            <SidebarLayout />
          </ProtectedLayout>
        ),
        children: [
          { path: 'seleccionar-sucursal', element: <SeleccionarSucursalPage /> },
          { path: '', element: <PermissionGuard permisos={['VER_DASHBOARD']} fallback={<Overview />}><Dashboard /></PermissionGuard> },
          { path: 'overview', element: <Navigate to="/" replace /> },
          {
            path: 'administracion',
            children: [
              { path: '', element: <Navigate to="usuarios" replace /> },
              { path: 'monedas', element: <PermissionGuard permisos={['MONEDA_GESTION']} requireSuperAdmin><MonedasPage /></PermissionGuard> },
              { path: 'empresas', element: <PermissionGuard permisos={['EMPRESA_VER']}><EmpresasPage /></PermissionGuard> },
              { path: 'sucursales', element: <PermissionGuard permisos={['SUCURSAL_GESTION']}><SucursalesPage /></PermissionGuard> },
              { path: 'roles', element: <PermissionGuard permisos={['ROL_GESTION']}><RolesPage /></PermissionGuard> },
              { path: 'permisos', element: <PermissionGuard permisos={['PERMISO_VER']} requireSuperAdmin><PermisosPage /></PermissionGuard> },
              { path: 'usuarios', element: <PermissionGuard permisos={['USUARIO_GESTION']}><UsuariosPage /></PermissionGuard> },
              { path: 'metodos-pago', element: <PermissionGuard permisos={['METODO_PAGO_GESTION']}><MetodosPagoPage /></PermissionGuard> },
              { path: 'tokens-tv', element: <PermissionGuard permisos={['GESTION_TOKEN_TV']}><TokensTvPage /></PermissionGuard> },
            ]
          },
          {
            path: 'produccion',
            children: [
              { path: '', element: <Navigate to="areas" replace /> },
              { path: 'areas', element: <PermissionGuard permisos={['AREA_GESTION']}><AreasPage /></PermissionGuard> },
              { path: 'categorias', element: <PermissionGuard permisos={['PRODUCTO_GESTION']}><CategoriasPage /></PermissionGuard> },
              { path: 'productos', element: <PermissionGuard permisos={['PRODUCTO_GESTION']}><ProductosPage /></PermissionGuard> },
            ]
          },
          {
            path: 'facturacion',
            children: [
              { path: '', element: <Navigate to="pos" replace /> },              
              { path: 'zonas', element: <PermissionGuard permisos={['ZONA_GESTION']}><ZonasPage /></PermissionGuard> },
              { path: 'mesas', element: <PermissionGuard permisos={['MESA_GESTION']}><MesasPage /></PermissionGuard> },
              { path: 'pos', element: <PermissionGuard permisos={['POS_USAR']}><POSPage /></PermissionGuard> },
              { path: 'pedidos', element: <Navigate to="/reportes/historial" replace /> },
              { path: 'pedidos-listos', element: <PermissionGuard permisos={['COMANDA_ACTUALIZAR']}><MeseroPedidosPage /></PermissionGuard> },
            ]
          },
          {
            path: 'comandas',
            children: [
              { path: '', element: <Navigate to="por-producto" replace /> },
              { path: 'por-area', element: <Navigate to="por-producto" replace /> },
              { path: 'por-producto', element: <PermissionGuard permisos={['COMANDA_VER']}><ComandasPage /></PermissionGuard> },
              { path: 'por-pedido', element: <PermissionGuard permisos={['COMANDA_VER']}><ComandasPorPedidoPage /></PermissionGuard> },
            ]
          },
          {
            path: 'reportes',
            children: [
              { path: '', element: <Navigate to="ventas-diarias" replace /> },
              { path: 'ventas-diarias', element: <PermissionGuard permisos={['REPORTE_VER']}><VentasDiariasPage /></PermissionGuard> },
              { path: 'pedidos-anulados', element: <PermissionGuard permisos={['PEDIDO_ANULAR_VER']}><PedidosAnuladosPage /></PermissionGuard> },
              { path: 'historial', element: <PermissionGuard permisos={['HISTORIAL_PEDIDO_VER']}><HistorialPedidosPage /></PermissionGuard> },
            ]
          },
          {
            path: 'status',
            children: [
              { path: '', element: <Navigate to="404" replace /> },
              { path: '404', element: <Status404 /> },
              { path: '500', element: <Status500 /> },
              { path: 'maintenance', element: <StatusMaintenance /> },
              { path: 'coming-soon', element: <StatusComingSoon /> }
            ]
          },
          { path: '*', element: <Status404 /> }
        ]
      }
    ]
  }
];

export default routes;
